import React, { useState, useEffect } from 'react';

interface Repository {
    name: string;
    url: string;
    description: string;
    stars?: number;
    last_update?: string;
}

const RepositoryCard: React.FC<{ repo: Repository; isDarkMode: boolean }> = ({ repo, isDarkMode }) => {
    // Format the date to a more readable format
    const formattedDate = repo.last_update
        ? new Date(repo.last_update).toLocaleDateString()
        : 'N/A';

    return (
        <a
            href={repo.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block group"
        >
            {/* Gradient Card with animated border glow on hover */}
            <div className={`relative rounded-2xl p-[1px] transition-all duration-500 hover:scale-[1.02] ${isDarkMode ? 'bg-gradient-to-br from-neutral-900 to-neutral-950' : 'bg-gradient-to-br from-neutral-200 to-neutral-100'}`}>
                {/* Gradient border that intensifies on hover */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm ${isDarkMode ? 'from-blue-500/20 via-purple-500/20 to-cyan-500/20' : 'from-blue-400/30 via-purple-400/30 to-cyan-400/30'}`}></div>

                {/* Card content */}
                <div className={`relative rounded-2xl p-5 h-full flex flex-col justify-between backdrop-blur-xl ${isDarkMode ? 'bg-neutral-900' : 'bg-white'}`}>
                    <div>
                        {/* Title with gradient text on hover */}
                        <h2 className={`text-xl font-semibold mb-3 transition-all duration-500 ${isDarkMode ? 'text-white group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:via-purple-400 group-hover:to-cyan-400 group-hover:bg-clip-text group-hover:text-transparent' : 'text-neutral-900 group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:via-purple-600 group-hover:to-cyan-600 group-hover:bg-clip-text group-hover:text-transparent'}`}>
                            {repo.name}
                        </h2>
                        {/* Description */}
                        <p className={`text-sm line-clamp-3 leading-relaxed ${isDarkMode ? 'text-neutral-300' : 'text-neutral-600'}`}>
                            {repo.description}
                        </p>
                    </div>

                    {/* Metadata with gradient accents */}
                    <div className={`flex justify-between items-center mt-4 pt-3 border-t ${isDarkMode ? 'border-neutral-800/50' : 'border-neutral-200/70'}`}>
                        <span className={`flex items-center gap-1.5 text-xs transition-colors duration-300 ${isDarkMode ? 'text-neutral-400 group-hover:text-yellow-400' : 'text-neutral-500 group-hover:text-yellow-600'}`}>
                            <span className="text-sm">⭐</span>
                            <span className="font-medium">{repo.stars !== undefined ? repo.stars.toLocaleString() : 'N/A'}</span>
                        </span>
                        <span className={`text-xs ${isDarkMode ? 'text-neutral-500' : 'text-neutral-400'}`}>
                            {formattedDate}
                        </span>
                    </div>
                </div>
            </div>
        </a>
    );
};

const App: React.FC = () => {
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [repositories, setRepositories] = useState<Repository[]>([]);
    const [filteredRepos, setFilteredRepos] = useState<Repository[]>([]);
    const [sortBy, setSortBy] = useState<'stars' | 'last_update' | 'name'>('stars');

    useEffect(() => {
        fetch('/output_mcp_servers.json')
            .then((response) => response.json())
            .then((data) => {
                setRepositories(data);
                setFilteredRepos(sortRepositories(data, sortBy));
            })
            .catch((error) => console.error('Error fetching repositories:', error));
    }, [sortBy]);

    useEffect(() => {
        const filtered = repositories.filter((repo) =>
            repo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            repo.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredRepos(sortRepositories(filtered, sortBy));
    }, [searchTerm, repositories, sortBy]);

    const sortRepositories = (repos: Repository[], criterion: 'stars' | 'last_update' | 'name') => {
        return [...repos].sort((a, b) => {
            if (criterion === 'stars') {
                // Handle missing stars: treat undefined as -1 to sort to end
                const aStars = a.stars ?? -1;
                const bStars = b.stars ?? -1;
                return bStars - aStars;
            }
            if (criterion === 'last_update') {
                // Handle missing dates: treat undefined as 0 (epoch) to sort to end
                const aTime = a.last_update ? new Date(a.last_update).getTime() : 0;
                const bTime = b.last_update ? new Date(b.last_update).getTime() : 0;
                return bTime - aTime;
            }
            return a.name.localeCompare(b.name);
        });
    };

    const toggleTheme = () => {
        setIsDarkMode(!isDarkMode);
    };

    return (
        <div className={`${isDarkMode ? 'dark' : ''}`}>
            {/* Main container with animated gradient background */}
            <div className={`relative min-h-screen overflow-hidden transition-colors duration-500 ${isDarkMode ? 'bg-black text-white' : 'bg-gradient-to-br from-blue-50 via-purple-50 to-cyan-50 text-neutral-900'}`}>
                {/* Animated gradient orbs - ambient breathing effect */}
                <div className="fixed inset-0 overflow-hidden pointer-events-none">
                    {/* Primary radial orb - top center (hub energy source) */}
                    <div className={`absolute top-[-20%] left-[50%] translate-x-[-50%] w-[800px] h-[800px] rounded-full blur-3xl animate-breathe ${isDarkMode ? 'bg-gradient-radial from-blue-500/30 via-purple-500/20 to-transparent' : 'bg-gradient-radial from-blue-300/40 via-purple-300/30 to-transparent'}`}></div>

                    {/* Secondary orb - bottom left */}
                    <div className={`absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full blur-3xl animate-breathe-delayed ${isDarkMode ? 'bg-gradient-radial from-cyan-500/20 via-blue-500/10 to-transparent' : 'bg-gradient-radial from-cyan-300/35 via-blue-300/25 to-transparent'}`}></div>

                    {/* Tertiary orb - right */}
                    <div className={`absolute top-[40%] right-[-15%] w-[700px] h-[700px] rounded-full blur-3xl animate-breathe-slow ${isDarkMode ? 'bg-gradient-radial from-purple-500/25 via-pink-500/15 to-transparent' : 'bg-gradient-radial from-purple-300/40 via-pink-300/25 to-transparent'}`}></div>
                </div>

                {/* Content layer */}
                <div className="relative z-10">
                    {/* Header with gradient title */}
                    <header className="flex justify-between items-center p-6 md:p-8">
                        <div>
                            <h1 className={`text-4xl md:text-5xl font-bold bg-gradient-to-r bg-clip-text text-transparent mb-2 animate-gradient-flow ${isDarkMode ? 'from-blue-400 via-purple-400 to-cyan-400' : 'from-blue-600 via-purple-600 to-cyan-600'}`}>
                                MCP Servers Hub
                            </h1>
                            <p className={`text-sm md:text-base ${isDarkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
                                Discover the energy of connected intelligence
                            </p>
                        </div>

                        {/* Gradient Theme Toggle */}
                        <button
                            onClick={toggleTheme}
                            className={`relative group px-6 py-3 rounded-full backdrop-blur-sm transition-all duration-300 hover:scale-105 ${isDarkMode ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 hover:from-blue-500/30 hover:to-purple-500/30 border border-blue-500/30' : 'bg-white/60 hover:bg-white/80 border border-blue-300/50 shadow-lg'}`}
                        >
                            <span className={`relative z-10 font-medium flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-neutral-900'}`}>
                                <span className="text-lg">{isDarkMode ? '☀️' : '🌙'}</span>
                                <span className="hidden sm:inline">{isDarkMode ? 'Light' : 'Dark'}</span>
                            </span>
                        </button>
                    </header>

                    {/* Main content */}
                    <div className="px-6 md:px-8 pb-12">
                        {/* Subtitle with gradient accent */}
                        <p className={`mb-8 text-lg max-w-4xl ${isDarkMode ? 'text-neutral-300' : 'text-neutral-700'}`}>
                            Explore the most comprehensive and up-to-date collection of
                            <span className={`text-transparent bg-gradient-to-r bg-clip-text font-semibold ${isDarkMode ? 'from-blue-400 to-cyan-400' : 'from-blue-600 to-cyan-600'}`}> Model Context Protocol </span>
                            servers — where human understanding meets machine logic.
                        </p>

                        {/* Search and Sort controls */}
                        <div className="mb-8 flex flex-col sm:flex-row gap-4">
                            {/* Gradient search input with glow on focus */}
                            <div className="relative flex-1 group">
                                <input
                                    type="text"
                                    placeholder="Search repositories..."
                                    className={`w-full px-5 py-3 rounded-2xl border backdrop-blur-xl focus:outline-none transition-all duration-300 ${isDarkMode ? 'bg-neutral-900/80 border-neutral-800 text-white placeholder-neutral-500 focus:border-blue-500/50 focus:shadow-[0_0_20px_rgba(59,130,246,0.3)]' : 'bg-white/80 border-neutral-300 text-neutral-900 placeholder-neutral-400 focus:border-blue-500 focus:shadow-[0_0_20px_rgba(59,130,246,0.2)]'}`}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {/* Search icon with gradient */}
                                <div className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors duration-300 ${isDarkMode ? 'text-neutral-500 group-focus-within:text-blue-400' : 'text-neutral-400 group-focus-within:text-blue-600'}`}>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                            </div>

                            {/* Gradient sort dropdown */}
                            <select
                                className={`px-5 py-3 rounded-2xl border backdrop-blur-xl cursor-pointer focus:outline-none transition-all duration-300 ${isDarkMode ? 'bg-neutral-900/80 border-neutral-800 text-white focus:border-purple-500/50 focus:shadow-[0_0_20px_rgba(168,85,247,0.3)]' : 'bg-white/80 border-neutral-300 text-neutral-900 focus:border-purple-500 focus:shadow-[0_0_20px_rgba(168,85,247,0.2)]'}`}
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as 'stars' | 'last_update' | 'name')}
                            >
                                <option value="stars">Most Stars</option>
                                <option value="last_update">Recently Updated</option>
                                <option value="name">Name</option>
                            </select>
                        </div>

                        {/* Repository cards grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filteredRepos.map((repo, index) => (
                                <RepositoryCard key={index} repo={repo} isDarkMode={isDarkMode} />
                            ))}
                        </div>

                        {/* No results message */}
                        {filteredRepos.length === 0 && (
                            <div className="text-center py-20">
                                <div className={`inline-block p-6 rounded-3xl backdrop-blur-xl border ${isDarkMode ? 'bg-gradient-to-br from-neutral-900/50 to-neutral-950/50 border-neutral-800/50' : 'bg-white/60 border-neutral-300/50 shadow-lg'}`}>
                                    <p className={`text-lg ${isDarkMode ? 'text-neutral-400' : 'text-neutral-700'}`}>No repositories found</p>
                                    <p className={`text-sm mt-2 ${isDarkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Try adjusting your search terms</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default App;
